import { AlertCircle, CheckCircle, XCircle, HelpCircle } from 'lucide-react'
import type { NotBookedData } from '../types'
import { cn } from '@/lib/utils'

interface NotBookedBreakdownProps {
  data: NotBookedData
}

interface CardProps {
  title: string
  icon: React.ElementType
  value: number
  subtitle: string
  iconColor?: string
  valueColor?: string
}

function Card({ title, icon: Icon, value, subtitle, iconColor = 'text-gray-400', valueColor = 'text-gray-900' }: CardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <p className={cn('text-3xl font-bold', valueColor)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  )
}

export function NotBookedBreakdown({ data }: NotBookedBreakdownProps) {
  if (data.total === 0) return null

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Billable — Not Booked</h3>
      <div className="grid grid-cols-4 gap-4">
        <Card
          title="Total"
          icon={AlertCircle}
          value={data.total}
          subtitle="Billable, no Workiz match"
          iconColor="text-orange-500"
          valueColor="text-orange-600"
        />
        <Card
          title="Relevant"
          icon={CheckCircle}
          value={data.relevant}
          subtitle={`${data.total > 0 ? ((data.relevant / data.total) * 100).toFixed(0) : 0}% of not booked`}
          iconColor="text-emerald-500"
          valueColor="text-emerald-600"
        />
        <Card
          title="Not Relevant"
          icon={XCircle}
          value={data.not_relevant}
          subtitle={`${data.total > 0 ? ((data.not_relevant / data.total) * 100).toFixed(0) : 0}% of not booked`}
          iconColor="text-red-500"
          valueColor="text-red-500"
        />
        <Card
          title="Unclear"
          icon={HelpCircle}
          value={data.unclear}
          subtitle={`${data.total > 0 ? ((data.unclear / data.total) * 100).toFixed(0) : 0}% of not booked`}
          iconColor="text-yellow-500"
          valueColor="text-yellow-600"
        />
      </div>
    </div>
  )
}
